/**
 * Edit Game Page Component
 * 
 * Protected page that allows editing of existing pull tab games.
 * Wraps the PayoutStructureBuilder with navigation controls and
 * pre-populates it with existing game data for editing.
 * 
 * Features:
 * - Navigation header with back to compare/select games functionality
 * - User-aware interface with logout capability
 * - Integration with PayoutStructureBuilder for game editing
 * - Game data loading and pre-population
 * - Responsive layout with consistent styling
 * - Protected route requiring authentication
 */

import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import PayoutStructureBuilder from '../features/payouts/PayoutStructureBuilder';
import { useAuth } from '../hooks/useAuth';
import { useGetApiGames } from '../api/generated/games/games';

export default function EditGame() {
  console.log('🚨 EditGame component is loading!');
  const navigate = useNavigate();
  const { gameId } = useParams<{ gameId: string }>();
  console.log('🚨 GameId from params:', gameId);
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [gameNotFound, setGameNotFound] = useState(false);
  
  // Get games to find the specific game data - only when authenticated
  const { data: gamesData, isLoading: gamesLoading, error: gamesError } = useGetApiGames(
    undefined, // No params needed
    {
      query: {
        enabled: !!user, // Only fetch when user is authenticated
      }
    }
  );

  // Debug logging
  console.log('EditGame Debug:', {
    user: !!user,
    authLoading,
    gamesLoading,
    gamesError,
    gamesData,
    gameId
  });

  useEffect(() => {
    if (!gamesLoading && gamesData) {
      const game = gamesData.games?.find(g => g.id === parseInt(gameId || ''));
      if (!game) {
        setGameNotFound(true);
      }
      setLoading(false);
    }
  }, [gamesData, gamesLoading, gameId]);

  const handleBackToCompare = () => {
    navigate('/compare');
  };

  if (authLoading || loading || gamesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading game data...</div>
      </div>
    );
  }

  if (gameNotFound || !gameId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm border-b">
          <div className="container mx-auto px-2 tablet:px-4 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleBackToCompare}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  ← Back to Compare Games
                </button>
                <h1 className="text-2xl font-bold text-red-600">Game Not Found</h1>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-gray-600">{user?.name}</span>
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-2 tablet:px-4 py-8">
          <div className="text-center">
            <div className="text-4xl mb-4">😞</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Game Not Found
            </h2>
            <p className="text-gray-600 mb-6">
              The game you're trying to edit doesn't exist or you don't have permission to edit it.
            </p>
            <button
              onClick={handleBackToCompare}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Back to Games
            </button>
          </div>
        </main>
      </div>
    );
  }

  const gameData = gamesData?.games?.find(g => g.id === parseInt(gameId));

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-2 tablet:px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToCompare}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                ← Back to Compare Games
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                Edit Game: {gameData?.name}
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-600">{user?.name}</span>
            </div>
          </div>
        </div>
      </header>

      <main>
        <PayoutStructureBuilder 
          initialGameData={gameData}
          mode="edit"
        />
      </main>
    </div>
  );
}